import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const HeroSlider = ({ language, selectedCountry }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  const slides = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1690983325192-a4c13c2e331d",
    imageAlt: "Close-up of fresh green coffee beans in burlap sack with wooden scoop on rustic table",
    title: { en: "Premium African Green Coffee Beans", zh: "优质非洲生咖啡豆" },
    subtitle: { en: "Direct from verified farms to your roastery", zh: "从认证农场直接到您的烘焙厂" },
    country: "all"
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1715138958579-4d4ea6eeafbb",
    imageAlt: "Ethiopian coffee farmer harvesting ripe red coffee cherries from lush green coffee plants in highland plantation",
    title: { en: "Ethiopian Coffee Excellence", zh: "埃塞俄比亚咖啡卓越品质" },
    subtitle: { en: "Birthplace of coffee with unique flavor profiles", zh: "咖啡的发源地，独特的风味特征" },
    country: "Ethiopia"
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1729106005068-8f1920d19ed8",
    imageAlt: "Aerial view of vast Kenyan coffee plantation with organized rows of coffee trees on rolling hills under blue sky",
    title: { en: "Kenyan AA Grade Beans", zh: "肯尼亚AA级咖啡豆" },
    subtitle: { en: "Renowned for bright acidity and complex flavors", zh: "以明亮的酸度和复杂的风味而闻名" },
    country: "Kenya"
  },
  {
    id: 4,
    image: "https://images.unsplash.com/photo-1531129018241-f3e4ca6dc562",
    imageAlt: "Nigerian coffee processing facility with workers sorting green coffee beans on large drying tables under covered structure",
    title: { en: "Nigerian Coffee Innovation", zh: "尼日利亚咖啡创新" },
    subtitle: { en: "Emerging market with sustainable practices", zh: "新兴市场，可持续实践" },
    country: "Nigeria"
  },
  {
    id: 5,
    image: "https://images.unsplash.com/photo-1624644567223-42683d2fb50a",
    imageAlt: "Ugandan coffee farmer woman in traditional dress holding basket full of freshly picked red coffee cherries with mountain backdrop",
    title: { en: "Ugandan Robusta & Arabica", zh: "乌干达罗布斯塔和阿拉比卡" },
    subtitle: { en: "High altitude beans with exceptional quality", zh: "高海拔咖啡豆，品质卓越" },
    country: "Uganda"
  },
  {
    id: 6,
    image: "https://images.unsplash.com/photo-1603508103182-4a3273bab28a",
    imageAlt: "Modern coffee export warehouse with stacked burlap sacks of green coffee beans ready for international shipping",
    title: { en: "Global Coffee Trade", zh: "全球咖啡贸易" },
    subtitle: { en: "Connecting African farms to Chinese markets", zh: "连接非洲农场与中国市场" },
    country: "all"
  }];


  const filteredSlides = selectedCountry === 'all' ?
  slides :
  slides?.filter((slide) => slide?.country === 'all' || slide?.country === selectedCountry);

  useEffect(() => {
    if (filteredSlides?.length > 0) {
      setCurrentSlide(0);
    }
  }, [selectedCountry, filteredSlides?.length]);

  useEffect(() => {
    // Trigger entrance animation after component mounts
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || filteredSlides?.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % filteredSlides?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, filteredSlides?.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + filteredSlides?.length) % filteredSlides?.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % filteredSlides?.length);
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
  };

  if (filteredSlides?.length === 0) {
    return (
      <div className="w-full h-[400px] lg:h-[500px] bg-muted rounded-lg flex items-center justify-center">
        <p className="text-muted-foreground">{language === 'en' ? 'No slides available for this country' : '该国家暂无幻灯片'}</p>
      </div>);

  }

  return (
    <div
      className={`slider-container relative w-full h-[400px] lg:h-[500px] rounded-lg overflow-hidden shadow-card transition-all duration-1000 ${
        isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
      }`}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      onMouseMove={handleMouseMove}>

      {filteredSlides?.map((slide, index) =>
      <div
        key={slide?.id}
        className={`slider-slide absolute inset-0 transition-all duration-1000 ease-out ${
        index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`
        }>

          {/* Enhanced Image with Ken Burns Effect */}
          <div className={`relative w-full h-full overflow-hidden ${
            index === currentSlide ? 'animate-ken-burns' : ''
          }`}>
            <Image
              src={slide?.image}
              alt={slide?.imageAlt}
              className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                index === currentSlide ? 'scale-110 animate-subtle-zoom' : 'scale-100'
              }`} />

            {/* Enhanced gradient overlays for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/30" />
          </div>

          {/* Enhanced text content with staggered animations and parallax */}
          <div
            className={`absolute bottom-0 left-0 right-0 p-6 lg:p-12 transition-all duration-700 delay-300 ${
            index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
            style={{
              transform: index === currentSlide
                ? `translateY(0px) translateX(${(mousePosition.x - 0.5) * 10}px)`
                : 'translateY(16px)'
            }}
          >
            <div className="max-w-7xl mx-auto">
              <div className="inline-block max-w-3xl rounded-2xl bg-black/35 backdrop-blur-md border border-white/10 px-5 py-4 lg:px-7 lg:py-6 shadow-2xl">
              <h2
                className={`text-2xl lg:text-4xl font-heading font-bold text-white mb-2 drop-shadow-[0_2px_12px_rgba(0,0,0,0.65)] transition-all duration-500 delay-500 ${
                index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
              }`}
                style={{
                  transform: index === currentSlide
                    ? `translateY(0px) translateX(${(mousePosition.x - 0.5) * 15}px)`
                    : 'translateY(8px)'
                }}
              >
                {slide?.title?.[language]}
              </h2>
              <p
                className={`text-base lg:text-lg text-white/90 max-w-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)] transition-all duration-500 delay-700 ${
                index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
              }`}
                style={{
                  transform: index === currentSlide
                    ? `translateY(0px) translateX(${(mousePosition.x - 0.5) * 8}px)`
                    : 'translateY(8px)'
                }}
              >
                {slide?.subtitle?.[language]}
              </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {filteredSlides?.length > 1 &&
      <>
          {/* Enhanced navigation buttons with premium effects */}
          <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/90 hover:bg-card backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg border border-white/10 group"
          aria-label="Previous slide">

            <Icon name="ChevronLeft" size={24} className="text-foreground/80 group-hover:text-foreground transition-colors duration-300" />
          </button>

          <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/90 hover:bg-card backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg border border-white/10 group"
          aria-label="Next slide">

            <Icon name="ChevronRight" size={24} className="text-foreground/80 group-hover:text-foreground transition-colors duration-300" />
          </button>

          {/* Enhanced slide indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 bg-card/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/10">
            {filteredSlides?.map((_, index) =>
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`relative transition-all duration-500 rounded-full ${
            index === currentSlide
              ? 'w-8 h-3 bg-primary shadow-lg'
              : 'w-3 h-3 bg-white/40 hover:bg-white/60 hover:scale-125'
            }`}
            aria-label={`Go to slide ${index + 1}`} >
            {index === currentSlide && (
              <div className="absolute inset-0 bg-primary rounded-full animate-pulse opacity-50"></div>
            )}
          </button>
          )}
          </div>

        </>
      }
    </div>);

};

export default HeroSlider;